import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';

import { readDomainParam } from '~components/Filters/domainParam';

export const QueryParamNames = {
  ChartType: 'chart_type',
  DateFrom: 'dateFrom',
  DateTo: 'dateTo',
  Crawlers: 'crawlers',
  Domain: 'domain',
  // additional parameters used in table
  Sort: 'sort',
  SortDirection: 'sort_direction',
  Page: 'page',
  PageSize: 'page_size',
  Query: 'q',
  QueryCondition: 'q_condition',
  AdaptiveType: 'adaptive_type',
  CacheHit: 'cache_hit',
  StatusCode: 'status_code',
  StatusCodeLow: 'status_code_low',
  StatusCodeHigh: 'status_code_high',
  Timedout: 'timedout',
  ResponseTimeHigh: 'response_time_high',
  ResponseTimeLow: 'response_time_low',
} as const;

type QueryParamName = typeof QueryParamNames[keyof typeof QueryParamNames];

const QueryParamNamesList = Object.values(QueryParamNames);

type AdaptiveType = 'desktop' | 'mobile';
type CacheHit = 0 | 1;
type SortDirection = 'ASC' | 'DESC';
type SortableFields = 'crawler' | 'url' | 'time' | 'adaptive_type' | 'status_code' | 'render_time';

export type CDNAnalyticQueryParams = Pick<AggregatedMetrics.BaseGraphQueryParams, 'domain' | 'timezone'> &
  Partial<{
    chart_type: 'volume' | 'response-time';
    dateFrom: Date;
    dateTo: Date;
    crawlers: string[];
    q: string;
    q_condition: string;
    sort: SortableFields;
    sort_direction: SortDirection;
    page: number;
    page_size: number;
    adaptive_type: AdaptiveType[];
    cache_hit: CacheHit[];
    status_code: number;
    status_code_low: number;
    status_code_high: number;
    timedout: boolean;
    response_time_high: number;
    response_time_low: number;
  }>;

export type CDNAnalyticsCrawlerRequestsOutput = Omit<
  CDNAnalyticQueryParams,
  'dateTo' | 'dateFrom' | 'crawlers' | 'adaptive_type' | 'cache_hit' | 'chart_type'
> & {
  date_from: string;
  date_to: string;
  crawlers?: string;
  adaptive_type?: string;
  cache_hit?: string;
};

export type CDNAnalyticsCrawlerVisitsOutput = Omit<
  AggregatedMetrics.BaseGraphQueryParams,
  'dateFrom' | 'dateTo' | 'chart_type'
> & {
  dateFrom: string;
  dateTo: string;
};

export type CDNAnalyticsQueryStrings = Partial<Omit<CDNAnalyticQueryParams, 'crawlers'>>;

export function isCDNAnalyticsParam(name: string): name is QueryParamName {
  return QueryParamNamesList.includes(name as QueryParamName);
}

export function buildCDNAnalyticsQueryParameters(data: Record<string, string>): CDNAnalyticsQueryStrings {
  return Object.entries(data).reduce<CDNAnalyticsQueryStrings>((acc, [name, value]) => {
    if (!isCDNAnalyticsParam(name)) return acc;

    const paramName = name as QueryParamName;

    if (paramName === QueryParamNames.ChartType) {
      acc[paramName] = value as 'volume' | 'response-time';
    }

    if (paramName === QueryParamNames.DateFrom || paramName === QueryParamNames.DateTo) {
      acc[paramName] = new Date(`${value}T00:00`);
      return acc;
    }

    if (paramName === QueryParamNames.AdaptiveType) {
      acc[paramName] = value.split(',') as AdaptiveType[];
      return acc;
    }

    if (paramName === QueryParamNames.CacheHit) {
      acc[paramName] = value.split(',').map(Number) as CacheHit[];
      return acc;
    }

    if (paramName === QueryParamNames.Domain) {
      const domain = readDomainParam(value);
      if (domain) acc[paramName] = domain;
      return acc;
    }

    if (paramName === QueryParamNames.Query || paramName === QueryParamNames.QueryCondition) {
      acc[paramName] = value;
      return acc;
    }

    if (
      paramName === QueryParamNames.StatusCode ||
      paramName === QueryParamNames.StatusCodeLow ||
      paramName === QueryParamNames.StatusCodeHigh ||
      paramName === QueryParamNames.ResponseTimeLow ||
      paramName === QueryParamNames.ResponseTimeHigh ||
      paramName === QueryParamNames.Page ||
      paramName === QueryParamNames.PageSize
    ) {
      acc[paramName] = Number(value);
      return acc;
    }

    if (paramName === QueryParamNames.Timedout) {
      acc[paramName] = value === 'true';
      return acc;
    }

    if (paramName === QueryParamNames.Sort) {
      acc[paramName] = value as SortableFields;
      return acc;
    }

    if (paramName === QueryParamNames.SortDirection) {
      acc[paramName] = value as SortDirection;
      return acc;
    }

    return acc;
  }, {});
}
